import {
  Array,
  Boolean,
  Object,
  String,
  Number,
  Runtype,
  Static,
  Undefined,
  InstanceOf,
  Null,
  Record,
  Unknown,
} from 'runtypes';
import { Route } from './utils';
import { TeacherRecord, TimingRecord } from './accountManagement';

const DateType = InstanceOf(Date);

export const moduleRoutes = (): { [name: string]: Route } => ({
  postModuleEvent: {
    method: 'post',
    path: '/module/event',
    requestBody: PostModuleEventRequestRecord,
    responseData: PostModuleEventResponseRecord,
  },
  getClassModuleEvents: {
    method: 'get',
    path: '/class/:classId/module/:moduleId/events',
    requestBody: Undefined,
    responseData: GetEventsResponseRecord(DateType),
  },
  getClassroomModuleEvents: {
    method: 'get',
    path: '/classroom/:classroomId/module/:moduleId/events',
    requestBody: Undefined,
    responseData: GetEventsResponseRecord(DateType),
  },
  getClassEvents: {
    method: 'get',
    path: '/class/:classId/events',
    requestBody: Undefined,
    responseData: GetEventsResponseRecord(DateType),
  },
  getClassroomEvents: {
    method: 'get',
    path: '/classroom/:classroomId/events',
    requestBody: Undefined,
    responseData: GetEventsResponseRecord(DateType),
  },
  getLibrary: {
    method: 'get',
    path: '/library',
    requestBody: Undefined,
    responseData: GetLibraryResponseRecord,
  },
  getDefinition: {
    method: 'get',
    path: '/module/:moduleId/definition',
    requestBody: Undefined,
    responseData: GetDefinitionResponseRecord,
  },
  getVersionDefinition: {
    method: 'get',
    path: '/version/:versionId/module/:moduleId/definition',
    requestBody: Undefined,
    responseData: GetVersionDefinitionResponseRecord,
  },
  getDefinitions: {
    method: 'get',
    path: '/definition',
    requestBody: Undefined,
    responseData: GetDefinitionsResponseRecord,
  },
  getVersionDefinitions: {
    method: 'get',
    path: '/version/:versionId/definition',
    requestBody: Undefined,
    responseData: GetVersionDefinitionsResponseRecord,
  },
  getModuleUsage: {
    method: 'get',
    path: '/module-usage',
    requestBody: Undefined,
    responseData: GetModuleUsageResponseRecord,
  },
  getClassStudentUsage: {
    method: 'get',
    path: '/class/:classId/student-usage',
    requestBody: Undefined,
    responseData: GetStudentUsageResponseRecord,
  },
  getRecentErrorReports: {
    method: 'get',
    path: '/error-report',
    requestBody: Undefined,
    responseData: RecentModuleEventResponseRecord,
  },
});

// The 2024 VR client always supplies a string value
// for runId, moduleId, and taskId.  However, ensure
// the server handles cases where thy are not supplied.
export const ModuleEventRecord = Object({
  sessionId: String,
  runId: String.or(Null).optional(),
  moduleId: String.or(Null).optional(),
  taskId: String.or(Null).optional(),
  action: String,
  properties: Record(String, Unknown),
});

// Properties for login event
// The client does supply all these, but ensure
// the server handles cases where they are not supplied.
// This is just a partial list.
export const LoginPropertiesRecord = Object({
  loginName: String,
  studentClassKey: String,
  loginMethod: String,
  hardwareId: String.or(Null).optional(),
  hardwareType: String.or(Null).optional(),
  productName: String.or(Null).optional(),
  applicationVersion: String.or(Null).optional(),
});

export const SetClassPropertiesRecord = Object({
  token: String,
  classId: Number,
  userId: Number,
  passBack: Unknown.optional(),
});

const RecentModuleEventResponseRecord = Array(
  Object({
    sessionId: String,
    runId: String,
    properties: Record(String, Unknown),
  }),
);

// Used for the 2024 version
const ProgressRecord = Record(String, Array(String.or(Null)));
// Used for the 2025 version
const ModuleProgressRecord = Object({
  moduleId: String,
  partIndex: Number,
  taskId: String,
  runId: String,
});
// Used for the 2025 version
const PassBackRecord = Object({
  loginEventId: Number,
  version: String,
});

export const PostModuleEventRequestRecord = ModuleEventRecord.or(Array(ModuleEventRecord)).or(
  // September 2025 version format
  Object({
    loginSessionId: String,
    events: Array(
      Object({
        action: String,
        properties: Record(String, Unknown),
      }),
    ),
  }),
);

const ClassChoiceRecord = Object({
  id: Number,
  externalName: String.or(Null),
  nickName: String.or(Null),
  section: String.or(Null),
});

export const PostModuleEventResponseRecord = Object({
  runId: String,
})
  .or(
    // Login messages or set-class response
    Object({
      progress: ProgressRecord.or(
        // 2025 version
        Array(ModuleProgressRecord),
      ),
      // entire state associated with this student and class
      state: Record(String, Unknown),
      // Server timestamp of most recent state update
      // This isn't really used anywhere.
      stateTime: InstanceOf(Date).or(String).or(Null),
    }),
  )
  .or(
    // 2025 version login response
    Object({
      token: String,
      userId: Number,
      passBack: PassBackRecord,
      classes: Array(ClassChoiceRecord),
    }),
  )
  .or(
    // backwards-compatibility for old login format
    ProgressRecord,
  );

const EventRecord = <T extends Runtype>(useDate: T) =>
  Object({
    id: Number,
    sessionId: String,
    moduleId: String,
    taskId: String,
    action: String,
    runId: String,
    properties: Record(String, Unknown),
    createdAt: useDate,
  });
const EventWithContextRecord = <T extends Runtype>(useDate: T) =>
  EventRecord(useDate).extend({
    version: String,
    classId: Number,
    userId: Number,
  });
export const GetEventsResponseRecord = <T extends Runtype>(useDate: T) =>
  Object({ events: Array(EventWithContextRecord(useDate)) });

export const SubjectRecord = Object({
  id: String,
  name: String,
  children: Array(String),
  parents: Array(String),
  modules: Array(String),
});
export const GetLibraryRequestRecord = Object({});
export const GetLibraryResponseRecord = Object({
  subjects: Array(SubjectRecord),
});

const TaskDefinitionRecord = Object({
  id: String,
  name: String,
  description: String,
  question: String, // HTML format
  answer: String.or(Number), // HTML format
  messages: Array(String), // plain text format
  images: Array(String), // File name
  skills: Array(String), // skill description
  snapshot: Boolean.optional(), // Default (missing) is "true"
  sceneType: String.optional(),
});
// A single modulePart (the tutorial) is marked with "0"
const PartRecord = Object({
  modulePart: Number,
  tasks: Array(TaskDefinitionRecord),
  image: String.optional(),
  implementationGuide: Object({
    url: String,
  }).optional(),
  video: Object({
    file: String.optional(),
    url: String.optional(),
  }).optional(),
  quizPoints: Number.optional(),
  quizRecommendation: String.optional(),
  type: String.optional(),
});

const ModuleDefinitionRecord = Object({
  id: String,
  name: String,
  subjectId: String,
  parts: Array(PartRecord),
  image: String.optional(),
  subtitle: String.optional(),
  alignedResourcesLink: String.optional(),
});
export const GetDefinitionResponseRecord = Object({
  module: ModuleDefinitionRecord,
});
export const GetVersionDefinitionResponseRecord = Object({
  module: ModuleDefinitionRecord,
  version: String,
});

export const VersionDefinitionRecord = Object({
  modules: Array(ModuleDefinitionRecord),
  features: Array(String),
});
export const GetDefinitionsRequestRecord = Object({});
export const GetDefinitionsResponseRecord = VersionDefinitionRecord;
export const GetVersionDefinitionsResponseRecord = VersionDefinitionRecord.and(
  Object({
    version: String,
  }),
);

const AggregateStatsRecord = Object({
  count: Number,
  mean: Number.or(Null),
  median: Number.or(Null),
  standardDeviation: Number.or(Null),
});
const FinishedTasksRecord = Object({
  productName: String.or(Null),
  contentIds: Array(String),
  // Subset of TaskDefinitionRecord
  tasks: Array(
    Object({
      id: String,
      modulePart: Number,
      number: Number,
    }),
  ),
  counts: Array(Number),
});
const HistogramRecord = Object({
  bin: Number,
  value: Number,
});
const TeacherStatisticsRecord = TeacherRecord.and(
  Object({
    classes: Array(Number),
    rosteredStudents: Number,
    medianTimeOnTask: Number.or(Null),
    startedHistogram: Array(HistogramRecord),
    finishedHistogram: Array(HistogramRecord),
    institutionIds: Array(Number),
    institutionNames: Array(String),
  }),
);
export const GetAModuleUsageResponseRecord = Object({
  id: String,
  // There may be several products for a given
  // moduleId.  Thus, we let the server specify the appropriate
  // name and subject, depending on the data itself.
  name: String,
  subjectId: String,
  startedTimeOnTask: AggregateStatsRecord,
  completedTimeOnTask: AggregateStatsRecord,
  finishedTasksCount: Array(FinishedTasksRecord),
});
export const GetModuleUsageResponseRecord = Object({
  modules: Array(GetAModuleUsageResponseRecord),
  minTimeSeconds: Number,
  registeredStudents: Number,
  activeStudents: Number,
  activeClasses: Number,
  activeTeachers: Number,
  unlicensedStudents: Number,
  studentTimes: AggregateStatsRecord,
  studentTimesExclude: AggregateStatsRecord,
  studentModuleFractionsCompleted: AggregateStatsRecord,
  crossModule: Array(Array(Number)),
  crossModuleExclude: Array(Array(Number)),
  crossModuleBins: Number,
  teachers: Array(TeacherStatisticsRecord),
  plans: Array(Unknown), // in typescript, any[]
}).and(TimingRecord);

export const AStudentStatsRecord = Object({
  taskCount: Number,
  timeOnPart: Number,
  startedTasks: Number,
  completedTasks: Number,
});
export const GetAStudentModuleUsageRecord = Object({
  // Use 0 for module without parts (like the tutorial).
  moduleParts: Record(String, AStudentStatsRecord),
});
export const GetAStudentUsageResponseRecord = Object({
  modules: Record(String, GetAStudentModuleUsageRecord),
});
export const GetStudentUsageResponseRecord = Object({
  students: Record(String, GetAStudentUsageResponseRecord),
});

export type LoginProperties = Static<typeof LoginPropertiesRecord>;
export type ModuleEventInput = Static<typeof ModuleEventRecord>;
export type PostModuleEventRequest = Static<typeof PostModuleEventRequestRecord>;
export type PostModuleEventResponse = Static<typeof PostModuleEventResponseRecord>;
export const EventRS = EventRecord(String);
// This is a serializable version of ModuleEvent in types/models.ts
export type ModuleEvent = Static<typeof EventRS>;
export const EventWithContextRS = EventWithContextRecord(String);
// This is a serializable version of ModuleEventWithUserClass in types/models.ts
export type ModuleEventWithUserClass = Static<typeof EventWithContextRS>;
export const GetEventsResponseRS = GetEventsResponseRecord(String);
export type GetEventsResponseSerialized = Static<typeof GetEventsResponseRS>;
export type Progress = Static<typeof ProgressRecord>;
export type PassBack = Static<typeof PassBackRecord>;
export type ClassChoice = Static<typeof ClassChoiceRecord>;
export type ModuleProgress = Static<typeof ModuleProgressRecord>;
export type Part = Static<typeof PartRecord>;
// This should eventually be a literal in the PartRecord definition
export enum PartType {
  supplement = 'supplement',
}
export type AggregateStats = Static<typeof AggregateStatsRecord>;
export type FinishedTasks = Static<typeof FinishedTasksRecord>;
export type Subject = Static<typeof SubjectRecord>;
export type ModuleDefinition = Static<typeof ModuleDefinitionRecord>;
export type GetDefinitionResponse = Static<typeof GetDefinitionResponseRecord>;
export type GetDefinitionsRequest = Static<typeof GetDefinitionsRequestRecord>;
export type GetDefinitionsResponse = Static<typeof GetDefinitionsResponseRecord>;
export type VersionDefinition = Static<typeof VersionDefinitionRecord>;
export type GetVersionDefinitionsResponse = Static<typeof GetVersionDefinitionsResponseRecord>;
export type GetLibraryRequest = Static<typeof GetLibraryRequestRecord>;
export type GetLibraryResponse = Static<typeof GetLibraryResponseRecord>;
export type TaskDefinition = Static<typeof TaskDefinitionRecord>;
export type GetAModuleUsageResponse = Static<typeof GetAModuleUsageResponseRecord>;
export type GetModuleUsageResponse = Static<typeof GetModuleUsageResponseRecord>;
export type GetAStudentUsageResponse = Static<typeof GetAStudentUsageResponseRecord>;
export type GetStudentUsageResponse = Static<typeof GetStudentUsageResponseRecord>;
export type TeacherStatistics = Static<typeof TeacherStatisticsRecord>;
